/**
 * Sign-in, step 2: the six digits. The sixth digit submits by itself; the
 * button is there for a person who pasted less or wants to press something.
 *
 * `ApiClient.submitCode` stores the pair in the native token store, and
 * `completeSignIn` claims the device for that account (`accountScope.enter`:
 * another person's local data is forgotten here, and the outbox stops sending
 * their queued writes under this one) and makes the gate's answer ready (new
 * account → stepper, existing → its progress fetched) before
 * `useSession().refresh()` flips the session. This screen then unmounts: the
 * gate renders what comes next.
 */
import { Field } from "@base-ui/react/field";
import {
  onboardingProgressOptions,
  profileKeys,
} from "@repo/client/data/profile";
import type { OnboardingProgress } from "@repo/contract/training";
import {
  "auth.code_change_email" as codeChangeEmail,
  "auth.code_field_label" as codeFieldLabel,
  "auth.code_heading" as codeHeading,
  "auth.code_lede" as codeLede,
  "auth.code_resend" as codeResend,
  "auth.code_resend_wait" as codeResendWait,
  "auth.code_resent" as codeResent,
  "auth.code_submit" as codeSubmit,
  "auth.code_submitting" as codeSubmitting,
} from "@repo/i18n";
import { useQueryClient } from "@tanstack/react-query";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useBackIntercept } from "../../app/back-button";
import { useApi } from "../../data/api";
import { accountScope } from "../../data/provider";
import { fromQueryDef } from "../../data/query-client";
import { useSession } from "../../data/session";
import { Button } from "../../ui/controls";
import { submitCodeWithRef } from "../share/landing";
import { AuthFrame } from "./auth-frame";
import type { CodeRequest } from "./email-screen";
import { appI18n } from "./i18n";
import {
  type CompleteSignInPorts,
  completeSignIn,
  isCompleteCode,
  normalizeCode,
  resendSecondsLeft,
} from "./sign-in";

export interface CodeScreenProps {
  onChangeEmail: () => void;
  /** A new code was sent: a new verification replaces the old one. */
  onResent: (request: CodeRequest) => void;
  request: CodeRequest;
}

const CODE_INPUT =
  "a-display h-16 w-full rounded-md bg-surface px-4 text-center text-3xl text-ink tabular-nums tracking-[0.4em] ring-1 ring-line-strong transition-shadow placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-accent data-invalid:ring-2 data-invalid:ring-danger";

export function CodeScreen(props: CodeScreenProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { refresh } = useSession();
  const locale = appI18n.useLocale();
  const errorMessage = appI18n.useErrorMessage();
  const { onChangeEmail, onResent, request } = props;

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const secondsLeft = resendSecondsLeft(request.sentAt, now);

  // Tick once a second while the cooldown runs; stop when it reaches zero.
  useEffect(() => {
    if (secondsLeft === 0) {
      return;
    }
    const timer = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const ports = useMemo<CompleteSignInPorts>(
    () => ({
      enterAccount: async (accountId: string) => {
        await accountScope.enter(accountId);
      },
      // One attempt: the tokens are already stored, and a failure here only
      // means the gate's own query (which does retry) decides instead. The
      // default retries would hold "Entrando…" for seconds on a 5xx.
      fetchOnboarding: () =>
        queryClient.fetchQuery({
          ...fromQueryDef(onboardingProgressOptions(api)),
          retry: false,
        }),
      refresh,
      seedOnboarding: (progress: OnboardingProgress) => {
        queryClient.setQueryData(profileKeys.onboarding, progress);
      },
      // D260: with the share link this device arrived through, if any.
      submitCode: (verificationId, value) =>
        submitCodeWithRef(api, verificationId, value),
    }),
    [api, queryClient, refresh]
  );

  const verify = useCallback(
    (value: string) => {
      setPending(true);
      setError(undefined);
      completeSignIn(ports, request.verificationId, value).catch(
        (caught: unknown) => {
          setError(errorMessage(caught));
          setPending(false);
        }
      );
    },
    [errorMessage, ports, request.verificationId]
  );

  const change = useCallback(
    (raw: string) => {
      const next = normalizeCode(raw);
      setCode(next);
      setError(undefined);
      setNotice(undefined);
      if (isCompleteCode(next) && !pending) {
        verify(next);
      }
    },
    [pending, verify]
  );

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isCompleteCode(code) && !pending) {
        verify(code);
      }
    },
    [code, pending, verify]
  );

  const resend = useCallback(() => {
    setResending(true);
    setError(undefined);
    setNotice(undefined);
    api
      .requestCode(request.email, locale)
      .then((output) => {
        setCode("");
        setNotice(codeResent());
        setNow(Date.now());
        onResent({
          email: request.email,
          sentAt: Date.now(),
          verificationId: output.verificationId,
        });
      })
      .catch((caught: unknown) => {
        setError(errorMessage(caught));
      })
      .finally(() => {
        setResending(false);
      });
  }, [api, errorMessage, locale, onResent, request.email]);

  // Android Back is this step's own Back: it returns to the email step. While
  // a submit is in flight it does nothing and still swallows the press —
  // leaving mid-verification would drop the answer. The email step registers
  // nothing, so Back there leaves the app, as it does on Today.
  const back = useCallback(() => {
    if (!pending) {
      onChangeEmail();
    }
  }, [onChangeEmail, pending]);
  useBackIntercept(true, back);

  return (
    <AuthFrame
      actions={
        <>
          <Button
            disabled={pending || !isCompleteCode(code)}
            size="lg"
            type="submit"
            variant="primary"
          >
            {pending ? codeSubmitting() : codeSubmit()}
          </Button>
          <Button
            disabled={pending || resending || secondsLeft > 0}
            onClick={resend}
            variant="ghost"
          >
            {secondsLeft > 0
              ? codeResendWait({ seconds: secondsLeft })
              : codeResend()}
          </Button>
          <Button disabled={pending} onClick={onChangeEmail} variant="ghost">
            {codeChangeEmail()}
          </Button>
        </>
      }
      heading={codeHeading()}
      lede={codeLede({ email: request.email })}
      onSubmit={submit}
    >
      <Field.Root className="flex flex-col gap-1.5" invalid={Boolean(error)}>
        <Field.Label className="font-semibold text-base text-ink">
          {codeFieldLabel()}
        </Field.Label>
        <Field.Control
          autoComplete="one-time-code"
          className={CODE_INPUT}
          enterKeyHint="go"
          inputMode="numeric"
          name="code"
          onValueChange={change}
          pattern="[0-9]*"
          placeholder="••••••"
          readOnly={pending}
          value={code}
        />
        {error ? (
          <Field.Error className="text-danger text-sm leading-snug" match>
            {error}
          </Field.Error>
        ) : null}
        <p aria-live="polite" className="text-ink-2 text-sm leading-snug">
          {notice}
        </p>
      </Field.Root>
    </AuthFrame>
  );
}
